"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.bandPartitionProblemSchema = exports.bandRangeSchema = exports.BAND_OXYGEN_STATUSES = exports.BAND_VITAL_CODES = void 0;
exports.rangesFromThresholds = rangesFromThresholds;
exports.validateBandPartition = validateBandPartition;
exports.thresholdsFromRanges = thresholdsFromRanges;
/**
 * `RPM-058` — a band set read as a partition of each vital's plausible span, and written back as the
 * threshold rows a publish stores (`J-292`, `S4-1-D48`).
 *
 * THE SUBJECT, in one sentence: **how the strict `LT`/`GT` rows of `classification_bands` divide a
 * vital's plausible values into bands, and the check that an edited division has no gap and no
 * overlap before it may be published.** The rows are what scores a reading (`bands.ts` on the
 * server); the ranges are what an administrator edits and what the validator reads.
 *
 * ── THE READING, WHICH IS THE ANSWER TO `S4-1-D48` ────────────────────────────────────────────
 *
 *   1. **The grid.** A vital's values are the decimals from `plausible_min` to `plausible_max` in
 *      steps of one unit in the last place the definition is recorded at (`decimals`). Every column
 *      that holds such a value — `plausible_min`, `plausible_max`, `boundary_value`,
 *      `value_numeric` — is `numeric(8, 2)`, so the step is 0.01 on every vital today.
 *   2. **The scope.** One partition per vital, and for `SPO2` one per band oxygen status:
 *      `cb_oxygen_scope` is `(vital_code = 'SPO2') = (oxygen_status IS NOT NULL)`.
 *   3. **A range is a closed interval of grid values** `[from, to]`, carrying one band: `CRITICAL`
 *      or `CONCERNING` with a direction, or `CLEAR` with none.
 *   4. **From the rows to the ranges.** A grid value is classified exactly as the server's
 *      `classifyAgainst` classifies it — a `LOW`/`LT` row matches `value < boundary`, a `HIGH`/`GT`
 *      row `value > boundary`, the most severe match wins, no match is `CLEAR` — and consecutive
 *      values with the same band and direction form one range. So `LT 92` ends its band at 91.99 and
 *      the next range starts at 92: a value exactly at a boundary is the less severe band.
 *   5. **The partition is valid** when, inside the plausible span, every grid value is claimed by
 *      exactly one range (no `GAP`, no `OVERLAP`), and the ranges run, from low to high,
 *      critical-low, concerning-low, clear, concerning-high, critical-high — each at most once and
 *      `CLEAR` exactly once (`SHAPE`). That order is what strict thresholds can express: "CLEAR is
 *      the absence of a band" (`cb_no_clear_row`), so it sits between the low and the high bands,
 *      and a more severe band lies further from it. The witness is the smallest grid value claimed
 *      by no range, or by two.
 *   6. **From the ranges to the rows.** A `LOW` range publishes `LT` the next range's `from`; a
 *      `HIGH` range publishes `GT` the previous range's `to`; `CLEAR` publishes nothing. For a valid
 *      partition the rows classify every grid value as the ranges do, and the round trip of the
 *      seeded set is the identity.
 *   7. **Between two grid values** — a converted reading such as 99.005 °F — the published rows
 *      decide, and strict thresholds give it to the more severe of the two neighbouring ranges: it is
 *      below the `LOW` boundary above it, or above the `HIGH` boundary below it.
 *
 * PURE, AND THE BROWSER'S TOO. It imports nothing but `zod`, so the editor and the publish route
 * validate with the same function and cannot disagree about a gap.
 */
const zod_1 = require("zod");
/**
 * The vital codes a band may name: `vital_code` less `ACVPU`, which is not classified by bands.
 * A plain list rather than an import of `db/rpm-enums.ts`, because this file is the browser's and the
 * enum module brings Drizzle with it; `band-partition.test.ts` holds the two lists in step.
 */
exports.BAND_VITAL_CODES = [
    "HR",
    "RR",
    "TEMP",
    "BP_SYSTOLIC",
    "BP_DIASTOLIC",
    "SPO2",
];
/** `band_oxygen_status`: no `UNKNOWN` (`CR-17`). */
exports.BAND_OXYGEN_STATUSES = ["ROOM_AIR", "ON_OXYGEN"];
/** The one vital whose bands carry an oxygen status: `cb_oxygen_scope`. */
const OXYGEN_SCOPED_VITAL = "SPO2";
/**
 * An endpoint is TEXT on the wire, bounded only in length. Whether it is a value of the vital — empty,
 * not a number, finer than the vital is recorded at, or ending before it starts — is the partition
 * check's to say (`validateBandPartition`), in its own words and under `BAND_UNREADABLE` or
 * `BAND_SHAPE`, rather than a `400` that names a pattern (`J-292` review). The grid's precision is
 * the definitions' `decimals`, and nothing here repeats it.
 */
const ENDPOINT = zod_1.z.string().max(32);
/** One closed interval of a vital's values and the band it scores. */
exports.bandRangeSchema = zod_1.z
    .strictObject({
    vitalCode: zod_1.z.enum(exports.BAND_VITAL_CODES),
    /** `ROOM_AIR` or `ON_OXYGEN` for `SPO2`, null for every other vital. */
    oxygenStatus: zod_1.z.enum(exports.BAND_OXYGEN_STATUSES).nullable(),
    severityBand: zod_1.z.enum(["CRITICAL", "CONCERNING", "CLEAR"]),
    /** `LOW` or `HIGH`, and null only for `CLEAR`. */
    direction: zod_1.z.enum(["LOW", "HIGH"]).nullable(),
    /** The lowest value the range claims, at the vital's precision. */
    from: ENDPOINT,
    /** The highest value the range claims, at the vital's precision. */
    to: ENDPOINT,
})
    .superRefine((range, ctx) => {
    if ((range.severityBand === "CLEAR") !== (range.direction === null))
        ctx.addIssue({
            code: "custom",
            path: ["direction"],
            message: "A clear band has no direction, and every other band has one.",
        });
    if ((range.vitalCode === OXYGEN_SCOPED_VITAL) !== (range.oxygenStatus !== null))
        ctx.addIssue({
            code: "custom",
            path: ["oxygenStatus"],
            message: "Oxygen saturation bands name room air or oxygen, and no other vital's do.",
        });
})
    .meta({ id: "BandRange", description: "A closed interval of one vital's values and its band" });
/** Why a partition cannot be published. */
exports.bandPartitionProblemSchema = zod_1.z
    .object({
    /**
     * No band claims the witness; two bands claim it; the bands are out of their one order; or an
     * endpoint is empty, not a number, or finer than the vital is recorded at.
     */
    kind: zod_1.z.enum(["GAP", "OVERLAP", "SHAPE", "UNREADABLE"]),
    vitalCode: zod_1.z.enum(exports.BAND_VITAL_CODES),
    oxygenStatus: zod_1.z.enum(exports.BAND_OXYGEN_STATUSES).nullable(),
    /**
     * The smallest value the problem is true of, at the vital's precision; for `UNREADABLE`, the
     * endpoint exactly as it was sent, which may be empty.
     */
    witness: zod_1.z.string(),
    /** In plain words: "92 is claimed by no band". */
    message: zod_1.z.string(),
})
    .meta({ id: "BandPartitionProblem", description: "One reason a band set cannot be published" });
// ── decimals as integers ─────────────────────────────────────────────────────────────────────
/** `"92.5"` at 2 decimals is 9250. Null when the text carries more decimals than the grid has. */
function toUnits(text, decimals) {
    const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(text.trim());
    if (match === null)
        return null;
    const [, sign, whole, fraction = ""] = match;
    const significant = fraction.replace(/0+$/, "");
    if (significant.length > decimals)
        return null;
    const units = Number(whole) * 10 ** decimals + Number(significant.padEnd(decimals, "0") || "0");
    return sign === "-" ? -units : units;
}
/** 9250 at 2 decimals is `"92.5"`; 9200 is `"92"`. */
function fromUnits(units, decimals) {
    const sign = units < 0 ? "-" : "";
    const abs = Math.abs(units);
    const scale = 10 ** decimals;
    const whole = Math.floor(abs / scale);
    const fraction = String(abs % scale)
        .padStart(decimals, "0")
        .replace(/0+$/, "");
    return `${sign}${whole}${fraction === "" ? "" : `.${fraction}`}`;
}
/** The decimal places a text carries, ignoring trailing zeros. */
function placesOf(text) {
    const fraction = text.split(".")[1] ?? "";
    return fraction.replace(/0+$/, "").length;
}
/** Compares two decimal texts exactly. */
function compareDecimal(a, b) {
    const places = Math.max(placesOf(a), placesOf(b));
    return (toUnits(a, places) ?? 0) - (toUnits(b, places) ?? 0);
}
/** A decimal text written once: no trailing zeros, no trailing point. */
function canonical(text) {
    const places = placesOf(text);
    return fromUnits(toUnits(text, places) ?? 0, places);
}
const scopesOf = (definition) => definition.vitalCode === OXYGEN_SCOPED_VITAL
    ? exports.BAND_OXYGEN_STATUSES.map((oxygenStatus) => ({
        vitalCode: definition.vitalCode,
        oxygenStatus,
    }))
    : [{ vitalCode: definition.vitalCode, oxygenStatus: null }];
const inScope = (scope) => (row) => row.vitalCode === scope.vitalCode && row.oxygenStatus === scope.oxygenStatus;
const SEVERITY_RANK = { CLEAR: 0, CONCERNING: 1, CRITICAL: 2 };
/** Where a band sits from low to high: the one order strict thresholds can express. */
function slotOf(range) {
    if (range.severityBand === "CLEAR")
        return 2;
    const away = range.severityBand === "CONCERNING" ? 1 : 2;
    return range.direction === "LOW" ? 2 - away : 2 + away;
}
// ── rows → ranges ────────────────────────────────────────────────────────────────────────────
/**
 * Today's threshold rows as the ranges that partition each scope's plausible span (clauses 1–4).
 * A scope with no rows is one `CLEAR` range. A row whose band claims no plausible value yields no
 * range, and the rows written back from these ranges do not carry it.
 */
function rangesFromThresholds(rows, definitions) {
    return definitions.flatMap((definition) => scopesOf(definition).flatMap((scope) => rangesOfScope(rows.filter(inScope(scope)), scope, definition)));
}
function rangesOfScope(rows, scope, definition) {
    const d = definition.decimals;
    const min = mustUnits(definition.plausibleMin, d);
    const max = mustUnits(definition.plausibleMax, d);
    const boundaries = rows.map((row) => ({ row, at: mustUnits(row.boundaryValue, d) }));
    // The band changes only where a row starts or stops matching: at `b` for `LT b`, at `b + 1` for `GT b`.
    const starts = new Set([min]);
    for (const { row, at } of boundaries) {
        const change = row.comparator === "LT" ? at : at + 1;
        if (change > min && change <= max)
            starts.add(change);
    }
    const sorted = [...starts].sort((a, b) => a - b);
    const classify = (value) => {
        let decided = null;
        for (const { row, at } of boundaries) {
            const matches = row.comparator === "LT" ? value < at : value > at;
            if (!matches)
                continue;
            if (decided === null || SEVERITY_RANK[row.severityBand] > SEVERITY_RANK[decided.severityBand])
                decided = row;
        }
        return decided === null
            ? { band: "CLEAR", direction: null }
            : { band: decided.severityBand, direction: decided.direction };
    };
    const ranges = [];
    sorted.forEach((start, index) => {
        const end = (sorted[index + 1] ?? max + 1) - 1;
        const { band, direction } = classify(start);
        const last = ranges[ranges.length - 1];
        if (last !== undefined && last.severityBand === band && last.direction === direction) {
            ranges[ranges.length - 1] = { ...last, to: fromUnits(end, d) };
            return;
        }
        ranges.push({
            vitalCode: scope.vitalCode,
            oxygenStatus: scope.oxygenStatus,
            severityBand: band,
            direction,
            from: fromUnits(start, d),
            to: fromUnits(end, d),
        });
    });
    return ranges;
}
function mustUnits(text, decimals) {
    const units = toUnits(text, decimals);
    if (units === null)
        throw new Error(`${text} carries more decimal places than the vital is recorded at (${decimals}).`);
    return units;
}
/**
 * Why an endpoint cannot be read as a value of this vital, in plain words, or null when it can. It
 * names the text as typed and never a number made up from it: an empty box is not 0.
 */
function unreadableBecause(text, decimals) {
    const typed = text.trim();
    if (typed === "")
        return "A boundary is empty.";
    if (!/^-?\d+(\.\d+)?$/.test(typed))
        return `“${typed}” is not a number.`;
    if (toUnits(typed, decimals) === null)
        return `${typed} has more decimal places than this vital is recorded at (${decimals}).`;
    return null;
}
// ── the check ────────────────────────────────────────────────────────────────────────────────
/**
 * Whether `ranges` partition every scope of `definitions` (clause 5), and if not, every reason.
 * Values outside the plausible span are not values (`RPM-010`), so a range reaching past the span is
 * read only inside it.
 */
function validateBandPartition(ranges, definitions) {
    const problems = [];
    const known = definitions.flatMap(scopesOf);
    for (const range of ranges) {
        if (known.some((scope) => inScope(scope)(range)))
            continue;
        problems.push({
            kind: "SHAPE",
            vitalCode: range.vitalCode,
            oxygenStatus: range.oxygenStatus,
            witness: range.from,
            message: "This band belongs to a vital this set does not classify.",
        });
    }
    for (const definition of definitions)
        for (const scope of scopesOf(definition))
            problems.push(...problemsOfScope(ranges.filter(inScope(scope)), scope, definition));
    return problems.length === 0 ? { ok: true } : { ok: false, problems };
}
function problemsOfScope(ranges, scope, definition) {
    const d = definition.decimals;
    const min = mustUnits(definition.plausibleMin, d);
    const max = mustUnits(definition.plausibleMax, d);
    const problem = (kind, at, message) => ({
        kind,
        vitalCode: scope.vitalCode,
        oxygenStatus: scope.oxygenStatus,
        witness: typeof at === "string" ? at : fromUnits(at, d),
        message,
    });
    const problems = [];
    const placed = [];
    for (const range of ranges) {
        const unreadable = [range.from, range.to]
            .map((text) => [text, unreadableBecause(text, d)])
            .filter((pair) => pair[1] !== null);
        for (const [text, why] of unreadable)
            problems.push(problem("UNREADABLE", text, why));
        const from = toUnits(range.from, d);
        const to = toUnits(range.to, d);
        if (unreadable.length > 0 || from === null || to === null)
            continue;
        if (from > to) {
            problems.push(problem("SHAPE", from, `The band starting at ${fromUnits(from, d)} ends at ${fromUnits(to, d)}, before it starts.`));
            continue;
        }
        placed.push({ range, from, to });
    }
    placed.sort((a, b) => a.from - b.from || a.to - b.to);
    // GAP and OVERLAP: one walk up the span, holding the highest value claimed so far.
    let claimedTo = min - 1;
    for (const placedRange of placed) {
        if (placedRange.to < min || placedRange.from > max) {
            // A band claiming no plausible value still sets a neighbour's boundary when the ranges are
            // written back as rows (`thresholdsFromRanges`), so it is refused, never skipped (`#603`).
            problems.push(problem("SHAPE", placedRange.from, `The band from ${fromUnits(placedRange.from, d)} to ${fromUnits(placedRange.to, d)} lies outside the values this vital is recorded at (${fromUnits(min, d)} to ${fromUnits(max, d)}).`));
            continue;
        }
        // Read only inside the span (the doc comment above): a range starting below the minimum claims
        // from the minimum, so it is never taken for an overlap with the span's start (`#603`'s review).
        const from = Math.max(placedRange.from, min);
        const to = Math.min(placedRange.to, max);
        if (from > claimedTo + 1) {
            const at = claimedTo + 1;
            problems.push(problem("GAP", at, `${fromUnits(at, d)} is claimed by no band.`));
        }
        else if (from <= claimedTo) {
            problems.push(problem("OVERLAP", from, `${fromUnits(from, d)} is claimed by two bands.`));
        }
        claimedTo = Math.max(claimedTo, to);
    }
    if (claimedTo < max) {
        const at = Math.max(claimedTo + 1, min);
        problems.push(problem("GAP", at, `${fromUnits(at, d)} is claimed by no band.`));
    }
    // SHAPE: the one order, each band at most once, CLEAR exactly once.
    let slot = -1;
    for (const { range, from } of placed) {
        const next = slotOf(range);
        if (next <= slot) {
            const name = range.severityBand === "CLEAR"
                ? "clear"
                : `${range.severityBand.toLowerCase()} (${(range.direction ?? "").toLowerCase()})`;
            problems.push(problem("SHAPE", Math.max(from, min), `The ${name} band starting at ${fromUnits(from, d)} is out of order: from low to high the bands run critical, concerning, clear, concerning, critical, each once.`));
        }
        slot = Math.max(slot, next);
    }
    if (!placed.some(({ range }) => range.severityBand === "CLEAR"))
        problems.push(problem("SHAPE", min, "There is no clear band: a value between the low and the high bands must score clear."));
    return problems;
}
// ── ranges → rows ────────────────────────────────────────────────────────────────────────────
/**
 * The threshold rows a valid partition publishes (clause 6): a `LOW` range is `LT` the next range's
 * `from`, a `HIGH` range `GT` the previous range's `to`. Run it only on a partition
 * `validateBandPartition` accepted; on any other the rows mean nothing.
 */
function thresholdsFromRanges(ranges) {
    const scopes = new Map();
    for (const range of ranges) {
        const key = `${range.vitalCode}/${range.oxygenStatus ?? ""}`;
        scopes.set(key, [...(scopes.get(key) ?? []), range]);
    }
    const rows = [];
    for (const scoped of scopes.values()) {
        const sorted = [...scoped].sort((a, b) => compareDecimal(a.from, b.from));
        sorted.forEach((range, index) => {
            if (range.severityBand === "CLEAR" || range.direction === null)
                return;
            const neighbour = range.direction === "LOW" ? sorted[index + 1] : sorted[index - 1];
            if (neighbour === undefined)
                return;
            rows.push({
                vitalCode: range.vitalCode,
                oxygenStatus: range.oxygenStatus,
                severityBand: range.severityBand,
                direction: range.direction,
                comparator: range.direction === "LOW" ? "LT" : "GT",
                boundaryValue: canonical(range.direction === "LOW" ? neighbour.from : neighbour.to),
            });
        });
    }
    return rows;
}
//# sourceMappingURL=band-partition.js.map